<template>
  <div>
    <div class="search-area">
      <form class="search-form" v-on:submit.prevent="doSearch">
        <div class="form-group">
          <label class="form-control-label device-id">車載器ID</label>
          <input
            type="text"
            class="form-control"
            v-model="state.search.deviceId"
          >
        </div>
        <div class="form-group">
          <label class="form-control-label car-name">車両番号</label>
          <input
            type="text"
            class="form-control"
            v-model="state.search.carName"
          >
        </div>
        <div class="form-group">
          <label class="form-control-label car-kind">車種</label>
          <select class="form-control" v-model="state.search.carKind">
            <option :value="null"></option>
            <option v-for="e in carKinds" :key="e.key" :value="e.key">
              {{e.val}}
            </option>
          </select>
        </div>
        <div class="form-group">
          <label class="form-control-label use-no-use">利用状況</label>
          <select class="form-control" v-model="state.search.usedStatus">
            <option value="">
              全て
            </option>
            <option value="1">
              利用中
            </option>
            <option value="0">
              利用停止中
            </option>
          </select>
        </div>
        <div class="form-group search-btn ta-center">
          <button type="submit" class="btn btn-primary">検索</button>
        </div>
      </form>
    </div>
    <AdminListTable
      :display-data="displayData"
      :fields="fields"
      @start-edit="edit"
      @start-destroy="tryDestroy"
    />
    <Modal
      v-if="state.showDestroyModal"
      :title="destroyModalTitle"
      :show-bottom-dismiss-button="true"
      @close="doDestroy"
      @dismiss="state.showDestroyModal = false"
    >
      <template v-slot:body>
        <div>
          <p class="p1">車載器ID<span class="colon"></span>{{state.destroyingDevice?.deviceId}}</p>
          <p class="p2 font-mtx-desc" v-html="destroyModalMsg"></p>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue';
import type { Device } from 'src/models/device';
import type { Lov } from 'src/models/master';
import type { AdminListTableField } from 'src/models/adminListTable';
import { lineBreakToBR } from 'src/lib/utils';
import { dtFormat } from 'src/lib/dateHelper';

type DisplayData = Device & {
  gName: string;
  carKindDisp: string;
  bikou1Disp: string;
  stoppedUseOnDisp: string;
};

interface SearchParams {
  deviceId: string;
  carName: string;
  carKind: string;
  usedStatus: string;
}

interface DeviceTableState {
  search: SearchParams;
  // 検索ボタン押下時に確定する検索条件。フィルタはこちらのみを参照する（入力中は自動検索しない）
  applied: SearchParams;
  showDestroyModal: boolean;
  destroyingDevice: Device | null;
}

const initSearchParams = (): SearchParams => ({
  deviceId: '',
  carName: '',
  carKind: '',
  usedStatus: '',
});

const props = defineProps<{
  devices: Device[];
  carKinds: Lov[];
}>();

const emit = defineEmits<{
  (e: 'edit', device: Device): void;
  (e: 'destroy', device: Device): void;
  (e: 'search'): void;
}>();

const state = reactive<DeviceTableState>({
  search: initSearchParams(),
  applied: initSearchParams(),
  showDestroyModal: false,
  destroyingDevice: null,
});

const doSearch = (): void => {
  emit('search');
  state.applied = { ...state.search };
};

const carKindMap = computed<Map<string, Lov>>(() => {
  return new Map(props.carKinds.map(e => [e.key, e]));
});

const filteredDevices = computed<Device[]>(() => {
  const qDeviceId = state.applied.deviceId;
  const qCarName = state.applied.carName;
  const qCarKind = state.applied.carKind;
  const qUsedStatus = parseInt(state.applied.usedStatus);
  return props.devices.filter(e => {
    if (qDeviceId && e.deviceId.indexOf(qDeviceId) === -1) return false;
    if (qCarName && e.carName.indexOf(qCarName) === -1) return false;
    if (qCarKind && e.carKind.indexOf(qCarKind) === -1) return false;
    if (!isNaN(qUsedStatus)) {
      // 利用中のみ抽出するので、停止されてたらはじく.
      if (qUsedStatus === 1 && e.stoppedUseOn) return false;
      // 利用停止中のみ抽出するので、停止してなかったらはじく.
      if (qUsedStatus === 0 && !e.stoppedUseOn) return false;
    }
    return true;
  });
});

const displayData = computed<DisplayData[]>(() => {
  return filteredDevices.value.map(device => {
    return {
      ...device,
      gName: lineBreakToBR(`${device.groupInfo.g2name}\n${device.groupInfo.g3name}`),
      carKindDisp: carKindMap.value.get(device.carKind)?.val ?? '',
      bikou1Disp: lineBreakToBR(device.bikou1),
      stoppedUseOnDisp: dtFormat(device.stoppedUseOn, 'yyyy/mm/dd'),
    };
  });
});

const edit = (obj: Device) => {
  emit('edit', obj);
};

const tryDestroy = (obj: Device) => {
  state.destroyingDevice = obj;
  state.showDestroyModal = true;
};

const doDestroy = () => {
  if (!state.destroyingDevice) return;

  emit('destroy', state.destroyingDevice);
  state.showDestroyModal = false;
};

const destroyModalTitle = '削除';
const destroyModalMsg = 'を削除してもよろしいですか？';
const fields: AdminListTableField<DisplayData>[] = [
  { name: 'gName', label: '所属組織', type: 'html', class: 'ws-nowrap' },
  { name: 'deviceId', label: '車載器ID', class: 'ws-nowrap' },
  { name: 'carName', label: '車両番号' },
  { name: 'carKindDisp', label: '車種' },
  { name: 'stoppedUseOnDisp', label: '利用停止日' },
  { name: 'bikou1Disp', label: '備考', type: 'html' },
];
</script>

<style lang="scss" scoped>

</style>
